import {
  add,
  sub,
  parse,
  format,
  startOfMonth,
  isBefore,
  differenceInCalendarWeeks,
  differenceInCalendarMonths,
  isValid,
  startOfWeek,
  isSameDay,
  endOfMonth,
} from "date-fns";

import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";

import { DateInfo, DateStatus, DayObject } from "../types";

export const getParsedDateObj = (
  date: string,
  clubWeekStartsOn: NumericalDayOfWeek
) => {
  if (!date) {
    return new Date();
  }

  const parsedDate = parse(date, "yyyy-MM-dd", new Date(), {
    weekStartsOn: clubWeekStartsOn,
  });

  if (isValid(parsedDate)) {
    return parsedDate;
  } else {
    return new Date();
  }
};

export const getStringifiedDate = (
  date: Date | null,
  clubWeekStartsOn: NumericalDayOfWeek
) => {
  if (!date || date.toString() === "Invalid Date") {
    return format(new Date(), "yyyy-MM-dd", { weekStartsOn: clubWeekStartsOn });
  }
  return format(date, "yyyy-MM-dd", { weekStartsOn: clubWeekStartsOn });
};

export const getFollowingMonth = (date: Date) => add(date, { months: 1 });

export const getFollowingMonthStart = (date: Date) => {
  const nextMonth = getFollowingMonth(date);

  return startOfMonth(nextMonth);
};
export const getPreviousMonth = (date: Date) => sub(date, { months: 1 });

export const getPreviousMonthEnd = (date: Date) => {
  const prevMonth = sub(date, { months: 1 });

  return endOfMonth(prevMonth);
};

/*
 * this function gives you the difference in calendar months between 2 dates.
 * positive if date1 is in a month greater than date2
 * negative if date1 is in a month less than date2
 * zero if the 2 dates are in the same month
 */
export const getDifferenceInMonthsBetweenTwoDates = (
  date1: Date,
  date2: Date
): number => {
  if (
    !date1 ||
    !date2 ||
    date1.toString() === "Invalid Date" ||
    date2.toString() === "Invalid Date"
  ) {
    return 0;
  }
  return differenceInCalendarMonths(date1, date2);
};

export const generateDateInfo = (
  startDate: Date,
  endDate: Date,
  status: DateStatus
): Map<string, DayObject> => {
  const dateInfoMap = new Map<string, DayObject>();
  let currentDate = startDate;

  while (isBefore(currentDate, endDate) || isSameDay(currentDate, endDate)) {
    const dateString = format(currentDate, "yyyy-MM-dd");
    dateInfoMap.set(dateString, { status: status });
    currentDate = add(currentDate, { days: 1 });
  }

  return dateInfoMap;
};

export const mergeDateInfo = (
  oldDateInfo: DateInfo | undefined,
  newDateInfo: DateInfo | undefined
): DateInfo => {
  if (!oldDateInfo && newDateInfo) {
    return newDateInfo;
  }
  if (oldDateInfo && !newDateInfo) {
    return oldDateInfo;
  }
  const newMap = new Map(oldDateInfo);

  newDateInfo?.forEach((dayObject, date) => {
    newMap.set(date, dayObject);
  });

  return newMap;
};

export const getWeeksBetweenInclusive = (
  startDate: Date,
  endDate: Date,
  clubWeekStartsOn: NumericalDayOfWeek
): number => {
  const start = startOfWeek(startDate, { weekStartsOn: clubWeekStartsOn });
  const end = startOfWeek(endDate, { weekStartsOn: clubWeekStartsOn });
  const weeksBetween = Math.abs(
    differenceInCalendarWeeks(start, end, {
      weekStartsOn: clubWeekStartsOn,
    })
  );

  return weeksBetween + 1;
};

export const possibleDateFormats = [
  "yyyy-MM-dd",
  "MMM do, yyyy",
  "MMMM do, yyyy",
  "MM/dd/yyyy",
  "M/d/yyyy",
  "MM-dd-yyyy",
  "MMMM d, yyyy",
  "MMM d, yyyy",
  "yyyy/MM/dd",
  "d/M/yyyy",
];

export const tryParseDate = (input: string) => {
  for (let i = 0; i < possibleDateFormats.length; i++) {
    const parsedDate = parse(input, possibleDateFormats[i], new Date());
    if (isValid(parsedDate)) {
      return parsedDate;
    }
  }
  return input;
};
